import { FC, useCallback, useMemo, useState } from "react";
import {
  Campaign,
  CampaignBody,
  CategorySensitivityColumns,
} from "@vviinn/user-managment-client-ts";
import { Fields } from "../../../../ui/layout/detailedPages/Fields";
import { NamedRow } from "../../../../components/GeneralUI/NamedRow/NamedRow";
import { Restricted } from "../../../../components/Restricted";
import { NamedSwitch } from "../../../../ui/layout/molecules/NamedSwitch";
import { parseNumberOrZero } from "../../../../helpers";
import {
  categorySensitivityTypeSettings,
  priceImpactNumericSettings,
  switchableSettingsTitles,
  textCampaignInfluencersTitles,
} from "../../../constants";
import {
  CategorySensitivityTitles,
  TextCampaignInfluencersKeys,
} from "../../../types";
import { NamedSelect } from "../../../../ui/layout/molecules/NamedSelect";
import { isActive, valueToTitle } from "../../../../helpers";
import { SlideOverWrapper } from "../../../../ui/layout/organisms/SlideOver/SlideOverWrapper/SlideOverWrapper";
import { IN_DEVELOPMENT } from "../../../../ui/constants";
import { UpdateCampaignProps } from "../../types";
import {
  CampaignNumberic,
  CampaignNumbericKey,
} from "../../../components/CampaignNumberic";
import { CollapsedFields } from "../../../../components/GeneralUI/CollapsedFields/CollapsedFields";

const allBooleanInfluencersTitles = {
  ...switchableSettingsTitles,
  ...textCampaignInfluencersTitles,
};
type BooleanInfluencersKeys = keyof typeof allBooleanInfluencersTitles;

const categoryKeys: (keyof CategorySensitivityTitles)[] = [
  "categorySensitivityColumn",
  "categorySensitivityWeight",
];

const exactSearchKeys: TextCampaignInfluencersKeys[] = [
  "exactSearchActive",
  "exactSearchOnly",
];

type InfluencerGroupKeys = "price" | "category" | "exact_search";

const configFromCampaign = (
  campaign: Campaign,
  groupKey: InfluencerGroupKeys,
) =>
  Object.fromEntries(
    (groupKey === "price"
      ? priceImpactNumericSettings
      : groupKey === "category"
        ? categoryKeys
        : exactSearchKeys
    ).map((key) => [key, campaign[key]]),
  );

export const Influencers: FC<UpdateCampaignProps> = ({
  campaign,
  updateCampaignBody,
}) => {
  const [slideOverOpen, setSlideOverOpen] = useState(false);
  const [slideOverType, setSlideOverType] = useState<InfluencerGroupKeys>();
  const [groupConfig, setGroupConfig] = useState<CampaignBody>({});
  const isVpsCampaign = campaign.type === "VPS";
  const isTextSearchCampaign = campaign.type === "TEXT_SEARCH";

  const handleOpen = (type: InfluencerGroupKeys) => {
    setSlideOverOpen(true);
    setSlideOverType(type);
  };

  const initConfig = useCallback(() => {
    slideOverOpen &&
      slideOverType &&
      setGroupConfig(configFromCampaign(campaign, slideOverType));
  }, [campaign, slideOverOpen, slideOverType]);

  const handleAccept = async (body: CampaignBody) => {
    return await updateCampaignBody(body);
  };

  const allowGroupSubmit = useMemo(
    () =>
      slideOverOpen &&
      slideOverType &&
      JSON.stringify(groupConfig) !==
        JSON.stringify(configFromCampaign(campaign, slideOverType)),
    [campaign, groupConfig, slideOverOpen, slideOverType],
  );

  const SwitchableNode = (key: BooleanInfluencersKeys) => (
    <NamedSwitch
      title={allBooleanInfluencersTitles[key]}
      value={campaign[key]}
      onChange={() => updateCampaignBody({ [key]: !campaign[key] })}
    />
  );

  const NumbericNode = (key: CampaignNumbericKey) => (
    <CampaignNumberic
      key={key}
      campaignKey={key}
      campaign={campaign}
      handleChange={(key) => (value) => {
        setGroupConfig((prev) => ({
          ...prev,
          [key]: parseNumberOrZero(String(value)),
        }));
      }}
    />
  );

  return (
    <Fields title="Influencers">
      {!isVpsCampaign && !isTextSearchCampaign && (
        <>
          <NamedRow
            title="Price impact"
            editPermissions="campaign:basic_config:update"
            content={!!campaign?.priceWeight ? "Active" : "Inactive"}
            onClick={() => handleOpen("price")}
            fixedHeight={true}
          />
          <NamedRow
            title="Category impact"
            editPermissions="campaign:basic_config:update"
            content={valueToTitle(
              !!campaign?.categorySensitivityWeight
                ? (campaign.categorySensitivityColumn ?? "No category")
                : "Inactive",
            )}
            onClick={() => handleOpen("category")}
            fixedHeight={true}
          />
        </>
      )}
      {isVpsCampaign && (
        <>
          {SwitchableNode("ocrActive")}
          {SwitchableNode("barcodeReaderActive")}
        </>
      )}
      {isTextSearchCampaign && (
        <Restricted permissions={IN_DEVELOPMENT}>
          <NamedRow
            title="Exact Search"
            editPermissions="campaign:basic_config:update"
            content={isActive(!!campaign?.exactSearchActive)}
            onClick={() => handleOpen("exact_search")}
            fixedHeight={true}
          />
          {SwitchableNode("diversificationActive")}
        </Restricted>
      )}
      <SlideOverWrapper
        open={slideOverOpen}
        initConfig={initConfig}
        onClose={() => setSlideOverOpen(false)}
        title={
          slideOverType === "price"
            ? "Price Impact"
            : slideOverType === "category"
              ? "Category Impact"
              : "Exact Search"
        }
        buttonProps={{
          disabled: !allowGroupSubmit,
          onClick: () => handleAccept(groupConfig),
        }}
      >
        {slideOverType === "price" ? (
          priceImpactNumericSettings.map((key) => NumbericNode(key))
        ) : slideOverType === "category" ? (
          <>
            <NamedSelect
              title="Category Sensitivity Type"
              onChange={({ value }) =>
                setGroupConfig((prev) => ({
                  ...prev,
                  categorySensitivityColumn:
                    value as CategorySensitivityColumns,
                }))
              }
              value={groupConfig.categorySensitivityColumn as string}
              options={categorySensitivityTypeSettings}
            />
            {NumbericNode("categorySensitivityWeight")}
          </>
        ) : (
          <CollapsedFields
            title="Exact Match First"
            isSwitchable={true}
            active={!!groupConfig.exactSearchActive}
            resetFields={() =>
              setGroupConfig((prev) => ({ ...prev, exactSearchActive: false }))
            }
            activateFields={() =>
              setGroupConfig((prev) => ({ ...prev, exactSearchActive: true }))
            }
          >
            <NamedSwitch
              title={allBooleanInfluencersTitles["exactSearchOnly"]}
              value={!!groupConfig.exactSearchOnly}
              onChange={(value) =>
                setGroupConfig((prev) => ({ ...prev, exactSearchOnly: value }))
              }
            />
          </CollapsedFields>
        )}
      </SlideOverWrapper>
      {SwitchableNode("onlyBuyable")}
      {!isVpsCampaign && !isTextSearchCampaign && (
        <>
          {SwitchableNode("ageGroupSensitive")}
          {SwitchableNode("genderSensitive")}
        </>
      )}
    </Fields>
  );
};
